// src/redux/auth/authSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

const BASE_URL = process.env.REACT_APP_BASE_URL || 'http://127.0.0.1:8000';

// Load persisted token from localStorage
const loadPersistedToken = () => {
  try {
    const token = localStorage.getItem('accessToken');
    return token;
  } catch {
    return null;
  }
};

/**
 * refreshAccessToken:
 * Calls /token/refresh/ which reads the refresh token from the cookie
 * and returns { access: <token> } if valid, else 401 if expired/invalid.
 */
export const refreshAccessToken = createAsyncThunk(
  'auth/refreshToken',
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetch(`${BASE_URL}/api/users/token/refresh/`, {
        method: 'POST',
        credentials: 'include',
      });

      if (!response.ok) {
        const errData = await response.json();
        if (response.status === 401) {
          // Don't treat this as an error for display purposes
          // It's expected when no valid refresh token exists
          return rejectWithValue({ silent: true, message: 'Refresh token expired' });
        }
        throw new Error(errData.message || 'Failed to refresh token');
      }

      const data = await response.json();
      // Store the new access token
      localStorage.setItem('accessToken', data.access);
      return data.access;
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

/**
 * loadUser - check if user is logged in using an access token
 */
export const loadUser = createAsyncThunk(
  'auth/loadUser',
  async ({ accessToken }, { rejectWithValue }) => {
    try {
      const response = await fetch(`${BASE_URL}/api/users/profile/`, {
        method: 'GET',
        credentials: 'include', // send cookies too
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (response.status === 401) {
        return null; // not logged in
      }
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.message || 'Failed to load user');
      }

      const userData = await response.json();
      return userData;
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

/**
 * login - normal approach
 */
export const login = createAsyncThunk(
  'auth/login',
  async ({ email, password }, { rejectWithValue }) => {
    try {
      let response = await fetch(`${BASE_URL}/api/users/login/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password }),
      });
      const data = await response.json();
      
      // Debug log
      console.log("Login response:", response.status, data);
      
      if (!response.ok) {
        // Return the error message directly
        return rejectWithValue(data.message || 'Login failed');
      }

      // Store the access token
      localStorage.setItem('accessToken', data.access);

      // Fetch user profile
      response = await fetch(`${BASE_URL}/api/users/profile/`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          Authorization: `Bearer ${data.access}`,
        },
      });
      if (!response.ok) {
        const errData = await response.json();
        return rejectWithValue(errData.message || 'Failed to fetch user profile');
      }

      const userData = await response.json();
      return {
        user: userData,
        access: data.access,
      };
    } catch (err) {
      console.error("Login error:", err);
      return rejectWithValue(err.message);
    }
  }
);

/**
 * logout - updated to send the access token in the header.
 */
export const logout = createAsyncThunk(
  'auth/logout',
  async (_, { rejectWithValue, getState }) => {
    try {
      const token = getState().auth.accessToken;
      const response = await fetch(`${BASE_URL}/api/users/logout/`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        credentials: 'include',
      });
      
      // Clear stored token regardless of response
      localStorage.removeItem('accessToken');
      
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.message || 'Logout failed');
      }
      
      return true;
    } catch (err) {
      // Even if logout fails on server, we want to clear local state
      return rejectWithValue(err.message);
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    user: null,
    accessToken: loadPersistedToken(), // Load token on init
    isAuthenticated: !!loadPersistedToken(), // Set initial auth state based on token
    loading: false,
    error: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // ------ refreshAccessToken ------
    builder.addCase(refreshAccessToken.fulfilled, (state, action) => {
      state.accessToken = action.payload;
      state.isAuthenticated = true;
      state.error = null;
    });
    builder.addCase(refreshAccessToken.rejected, (state, action) => {
      state.accessToken = null;
      state.user = null;
      state.isAuthenticated = false;
      // Only set error if it's not a silent error (like an expired refresh token)
      if (typeof action.payload === 'object' && action.payload.silent) {
        // Don't set error for silent failures like expired refresh tokens
      } else {
        state.error = typeof action.payload === 'object' ? action.payload.message : action.payload || 'Refresh token error';
      }
      localStorage.removeItem('accessToken');
    });

    // ------ loadUser ------
    builder.addCase(loadUser.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(loadUser.fulfilled, (state, action) => {
      state.loading = false;
      state.error = null;
      if (action.payload === null) {
        state.isAuthenticated = false;
        state.user = null;
      } else {
        state.isAuthenticated = true;
        state.user = action.payload;
      }
    });
    builder.addCase(loadUser.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
      state.isAuthenticated = false;
      state.user = null;
    });

    // ------ login ------
    builder.addCase(login.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(login.fulfilled, (state, action) => {
      state.loading = false;
      state.error = null;
      state.isAuthenticated = true;
      state.user = action.payload.user;
      state.accessToken = action.payload.access;
    });
    builder.addCase(login.rejected, (state, action) => {
      state.loading = false;
      state.isAuthenticated = false;
      state.user = null;
      state.accessToken = null;
      
      // Log the error payload to debug
      console.log("Login rejected payload:", action.payload);
      
      // Set the error message directly from payload
      state.error = action.payload || 'Authentication failed';
    });

    // ------ logout ------
    builder.addCase(logout.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(logout.fulfilled, (state) => {
      state.loading = false;
      state.error = null;
      state.isAuthenticated = false;
      state.user = null;
      state.accessToken = null;
    });
    builder.addCase(logout.rejected, (state, action) => {
      state.loading = false;
      state.isAuthenticated = false;
      state.user = null;
      state.accessToken = null;
      state.error = action.payload;
    });
  },
});

export const { clearError } = authSlice.actions;
export default authSlice.reducer;